import React, { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Save, Trash2, Plus, Search, DollarSign, Users, Calendar, Download,
  Eye, Edit2, X, Check, ChevronDown, ChevronUp, FileText, Send
} from 'lucide-react';
import manualBudgetService from '../services/manualBudgetService';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../config/permissions';
import toast from 'react-hot-toast';
import BudgetTemplatePreview from './BudgetTemplatePreview';

const UNITS = ['un', 'pç', 'm²', 'm', 'kg', 'lt', 'ch', 'sv', 'hora'];

const DEFAULT_TERMS = [
  'Orçamento válido por 30 dias',
  'Medidas sujeitas a verificação em obra',
  'Garantia de 12 meses contra defeitos de fabricação',
  'Instalação inclusa no valor acima'
];

const ManualBudgetCreator = ({ budgetId, onClose, onSave }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canManage = hasPermission(user?.role, 'gerencia') || hasPermission(user?.role, 'admin');

  const { data: existingBudget, isLoading } = useQuery({
    queryKey: ['manual-budget', budgetId],
    queryFn: () => manualBudgetService.getBudget(budgetId),
    enabled: !!budgetId
  });

  const [activeTab, setActiveTab] = useState('client');
  const [showPreview, setShowPreview] = useState(false);
  const [isEditing, setIsEditing] = useState(!budgetId);

  const [clientData, setClientData] = useState({
    name: '',
    phone: '',
    email: '',
    address: ''
  });

  const [projectDescription, setProjectDescription] = useState('');
  const [items, setItems] = useState([]);
  const [paymentConditions, setPaymentConditions] = useState([
    { type: 'VISTA', installments: 1, installmentAmount: 0, totalAmount: 0, description: 'À vista - 8% desconto', displayOrder: 0 },
    { type: 'PARCELADO', installments: 3, installmentAmount: 0, totalAmount: 0, interestRate: 0, description: '', displayOrder: 1 },
    { type: 'PARCELADO', installments: 6, installmentAmount: 0, totalAmount: 0, interestRate: 1.5, description: '', displayOrder: 2 }
  ]);
  const [discount, setDiscount] = useState({ type: 'PERCENT', value: 0 });
  const [validityDays, setValidityDays] = useState(30);
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState(DEFAULT_TERMS);

  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    quantity: 1,
    unit: 'un',
    unitPrice: 0
  });

  const [expandedConditions, setExpandedConditions] = useState({});

  const [editingCondition, setEditingCondition] = useState(null);
  const [newCondition, setNewCondition] = useState({
    type: 'VISTA',
    name: '',
    installments: 1,
    interestRate: 0,
    description: '',
    downPaymentValue: 0
  });

  const [companyInfo, setCompanyInfo] = useState({
    name: 'SISTEMA SANTIAGO',
    email: 'moveis@santiago.com.br',
    phone: '(11) 99999-9999'
  });

  React.useEffect(() => {
    if (existingBudget?.data) {
      const data = existingBudget.data;
      setClientData({
        name: data.client_name || '',
        phone: data.client_phone || '',
        email: data.client_email || '',
        address: data.client_address || ''
      });
      setProjectDescription(data.project_description || '');
      setItems(data.items || []);
      setPaymentConditions(data.paymentConditions?.length > 0
        ? data.paymentConditions.map((c, i) => ({ ...c, displayOrder: i }))
        : paymentConditions
      );
      setDiscount({
        type: data.discount_type || 'NONE',
        value: data.discount_value || 0
      });
      setValidityDays(data.validity_days || 30);
      setNotes(data.notes || '');
      setTerms(data.terms?.map(t => t.term_text || t) || DEFAULT_TERMS);
      setIsEditing(false);
    }
  }, [existingBudget]);

  const subtotal = useMemo(() => {
    return items.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0);
  }, [items]);

  const total = useMemo(() => {
    if (discount.type === 'PERCENT') {
      return subtotal * (1 - discount.value / 100);
    }
    if (discount.type === 'FIXED') {
      return Math.max(0, subtotal - discount.value);
    }
    return subtotal;
  }, [subtotal, discount]);

  const createMutation = useMutation({
    mutationFn: (data) => manualBudgetService.createBudget(data),
    onSuccess: (response) => {
      toast.success('Orçamento criado com sucesso!');
      queryClient.invalidateQueries(['manual-budgets']);
      if (onSave) onSave(response.data);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Erro ao criar orçamento');
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data) => manualBudgetService.updateBudget(budgetId, data),
    onSuccess: (response) => {
      toast.success('Orçamento atualizado com sucesso!');
      queryClient.invalidateQueries(['manual-budgets']);
      queryClient.invalidateQueries(['manual-budget', budgetId]);
      setIsEditing(false);
      if (onSave) onSave(response.data);
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Erro ao atualizar orçamento');
    }
  });

  const addItem = () => {
    if (!newItem.name.trim()) {
      toast.error('Nome do item é obrigatório');
      return;
    }

    const item = {
      id: `temp-${Date.now()}`,
      name: newItem.name,
      description: newItem.description,
      quantity: newItem.quantity || 1,
      unit: newItem.unit || 'un',
      unitPrice: newItem.unitPrice || 0,
      totalPrice: (newItem.quantity || 1) * (newItem.unitPrice || 0)
    };

    setItems([...items, item]);
    setNewItem({ name: '', description: '', quantity: 1, unit: 'un', unitPrice: 0 });
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const updatePaymentCondition = (index, field, value) => {
    const updated = [...paymentConditions];
    updated[index] = { ...updated[index], [field]: value };

    if (field === 'installments' || field === 'totalAmount' || field === 'interestRate') {
      const baseAmount = total;
      const installments = updated[index].installments || 1;
      const interestRate = updated[index].interestRate || 0;

      if (interestRate > 0) {
        const monthlyRate = interestRate / 100;
        updated[index].installmentAmount = baseAmount * (monthlyRate * Math.pow(1 + monthlyRate, installments)) /
          (Math.pow(1 + monthlyRate, installments) - 1);
      } else {
        updated[index].installmentAmount = baseAmount / installments;
      }
    }

    setPaymentConditions(updated);
  };

  const addPaymentCondition = () => {
    const conditionType = newCondition.type;
    let installments = newCondition.installments;
    let interestRate = newCondition.interestRate || 0;
    let downPaymentValue = newCondition.downPaymentValue || 0;
    let description = newCondition.description || '';

    let installmentAmount = total;
    if (conditionType === 'VISTA') {
      installments = 1;
      installmentAmount = total;
      if (description.includes('%')) {
        const pct = parseFloat(description) || 0;
        installmentAmount = total * (1 - pct / 100);
      } else if (newCondition.name.toLowerCase().includes('desconto')) {
        installmentAmount = total * 0.92;
      }
    } else if (conditionType === 'ENTRADA_PARCELADO') {
      const baseWithoutEntry = total - downPaymentValue;
      if (interestRate > 0) {
        const monthlyRate = interestRate / 100;
        const remainingInstallments = installments - 1;
        installmentAmount = baseWithoutEntry * (monthlyRate * Math.pow(1 + monthlyRate, remainingInstallments)) /
          (Math.pow(1 + monthlyRate, remainingInstallments) - 1);
      } else {
        installmentAmount = baseWithoutEntry / (installments - 1);
      }
    } else {
      if (interestRate > 0) {
        const monthlyRate = interestRate / 100;
        installmentAmount = total * (monthlyRate * Math.pow(1 + monthlyRate, installments)) /
          (Math.pow(1 + monthlyRate, installments) - 1);
      } else {
        installmentAmount = total / installments;
      }
    }

    const condition = {
      type: conditionType,
      name: newCondition.name || (conditionType === 'VISTA' ? 'À Vista' : conditionType === 'PARCELADO' ? `${installments}x sem juros` : `${installments}x com entrada`),
      installments: conditionType === 'VISTA' ? 1 : installments,
      installmentAmount,
      totalAmount: total,
      interestRate,
      downPaymentValue,
      description,
      displayOrder: paymentConditions.length
    };

    setPaymentConditions([...paymentConditions, condition]);
    setNewCondition({ type: 'VISTA', name: '', installments: 1, interestRate: 0, description: '', downPaymentValue: 0 });
  };

  const removePaymentCondition = (index) => {
    setPaymentConditions(paymentConditions.filter((_, i) => i !== index));
  };

  const movePaymentCondition = (index, direction) => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === paymentConditions.length - 1) return;

    const updated = [...paymentConditions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    updated.forEach((c, i) => c.displayOrder = i);
    setPaymentConditions(updated);
  };

  const updateConditionField = (index, field, value) => {
    const updated = [...paymentConditions];
    updated[index] = { ...updated[index], [field]: value };

    if (field === 'installments' || field === 'interestRate' || field === 'downPaymentValue' || field === 'type') {
      const baseAmount = total;
      const cond = updated[index];
      const condType = cond.type || 'PARCELADO';
      let installments = cond.installments || 1;
      let interestRate = cond.interestRate || 0;
      let downPaymentValue = cond.downPaymentValue || 0;

      if (condType === 'VISTA') {
        installments = 1;
        cond.installmentAmount = cond.description?.includes('%')
          ? baseAmount * (1 - parseFloat(cond.description) / 100)
          : baseAmount;
      } else if (condType === 'ENTRADA_PARCELADO') {
        const baseWithoutEntry = baseAmount - downPaymentValue;
        if (interestRate > 0) {
          const monthlyRate = interestRate / 100;
          const remainingInstallments = installments - 1;
          cond.installmentAmount = baseWithoutEntry * (monthlyRate * Math.pow(1 + monthlyRate, remainingInstallments)) /
            (Math.pow(1 + monthlyRate, remainingInstallments) - 1);
        } else {
          cond.installmentAmount = baseWithoutEntry / (installments - 1);
        }
      } else {
        if (interestRate > 0) {
          const monthlyRate = interestRate / 100;
          cond.installmentAmount = baseAmount * (monthlyRate * Math.pow(1 + monthlyRate, installments)) /
            (Math.pow(1 + monthlyRate, installments) - 1);
        } else {
          cond.installmentAmount = baseAmount / installments;
        }
      }
    }

    setPaymentConditions(updated);
  };

  const getConditionName = (condition) => {
    if (condition.name) return condition.name;
    if (condition.type === 'VISTA') return 'À Vista';
    if (condition.type === 'ENTRADA_PARCELADO') return `${condition.installments}x com entrada`;
    return `${condition.installments}x`;
  };

  const getConditionColor = (type) => {
    if (type === 'VISTA') return 'bg-emerald-500';
    if (type === 'ENTRADA_PARCELADO') return 'bg-amber-500';
    return 'bg-blue-500';
  };

  const calculateVistaAmount = () => {
    const vistaCondition = paymentConditions.find(c => c.type === 'VISTA');
    if (vistaCondition?.description?.includes('8%')) {
      return total * 0.92;
    }
    return total;
  };

  const handleSave = () => {
    if (!clientData.name.trim()) {
      toast.error('Nome do cliente é obrigatório');
      setActiveTab('client');
      return;
    }

    if (items.length === 0) {
      toast.error('Adicione pelo menos um item');
      setActiveTab('items');
      return;
    }

    const budgetData = {
      client: clientData,
      projectDescription,
      items: items.map((item, index) => ({
        name: item.name,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unitPrice: item.unitPrice
      })),
      paymentConditions: paymentConditions.map(c => ({
        type: c.type,
        name: c.name,
        installments: c.installments,
        installmentAmount: c.installmentAmount,
        totalAmount: c.totalAmount || total,
        interestRate: c.interestRate || 0,
        downPaymentValue: c.downPaymentValue || 0,
        description: c.description
      })),
      discount,
      validityDays,
      notes,
      terms: terms.map((text, index) => ({ text, order: index })),
      companyInfo
    };

    if (budgetId) {
      updateMutation.mutate(budgetData);
    } else {
      createMutation.mutate(budgetData);
    }
  };

  const generatePDFMutation = useMutation({
    mutationFn: () => manualBudgetService.generatePDF(budgetId),
    onSuccess: (response) => {
      if (response.data?.downloadUrl) {
        window.open(response.data.downloadUrl, '_blank');
      }
      toast.success('PDF gerado com sucesso!');
    },
    onError: (error) => {
      toast.error('Erro ao gerar PDF');
    }
  });

  const budgetData = useMemo(() => ({
    budget_number: existingBudget?.data?.budget_number || `ORC-${new Date().getFullYear()}-0001`,
    client_name: clientData.name,
    client_phone: clientData.phone,
    client_email: clientData.email,
    client_address: clientData.address,
    project_description: projectDescription,
    items,
    paymentConditions,
    discount,
    validity_days: validityDays,
    total_amount: total,
    created_at: existingBudget?.data?.created_at || new Date().toISOString(),
    notes
  }), [clientData, projectDescription, items, paymentConditions, discount, validityDays, total, existingBudget]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <FileText className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {budgetId ? 'Editar Orçamento' : 'Novo Orçamento Manual'}
            </h2>
            {existingBudget?.data?.budget_number && (
              <p className="text-sm text-gray-600">
                {existingBudget.data.budget_number}
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className={`px-4 py-2 border rounded-lg flex items-center gap-2 font-medium transition-all ${
              showPreview
                ? 'bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700'
                : 'border-emerald-600 text-emerald-600 hover:bg-emerald-50'
            }`}
          >
            <Eye className="w-4 h-4" />
            {showPreview ? 'Fechar Preview' : 'Pré-visualizar'}
          </button>
          <button
            onClick={() => generatePDFMutation.mutate()}
            disabled={generatePDFMutation.isLoading || !budgetId}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            Baixar PDF
          </button>
          {isEditing ? (
            <>
              <button
                onClick={() => setIsEditing(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={createMutation.isLoading || updateMutation.isLoading}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
              >
                <Check className="w-4 h-4" />
                Salvar
              </button>
            </>
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Editar
            </button>
          )}
        </div>
      </div>

      {showPreview ? (
        <div className="mb-6">
          <BudgetTemplatePreview budgetData={budgetData} />
        </div>
      ) : (
        <>
          <div className="flex gap-2 mb-6 border-b">
            {['client', 'items', 'payment', 'terms'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                  activeTab === tab
                    ? 'border-emerald-600 text-emerald-600'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab === 'client' && 'Cliente'}
                {tab === 'items' && `Itens (${items.length})`}
                {tab === 'payment' && 'Pagamento'}
                {tab === 'terms' && 'Termos'}
              </button>
            ))}
          </div>

          {activeTab === 'client' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Dados do Cliente</h3>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={clientData.name}
                    onChange={(e) => setClientData({ ...clientData, name: e.target.value })}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                    placeholder="Nome completo"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone
                  </label>
                  <input
                    type="text"
                    value={clientData.phone}
                    onChange={(e) => setClientData({ ...clientData, phone: e.target.value })}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                    placeholder="(11) 99999-9999"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={clientData.email}
                    onChange={(e) => setClientData({ ...clientData, email: e.target.value })}
                    disabled={!isEditing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                    placeholder="email@exemplo.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço
                  </label>
                  <textarea
                    value={clientData.address}
                    onChange={(e) => setClientData({ ...clientData, address: e.target.value })}
                    disabled={!isEditing}
                    rows={2}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                    placeholder="Rua, número, bairro, cidade"
                  />
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Descrição do Projeto</h3>
                <textarea
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  disabled={!isEditing}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                  placeholder="Descreva o projeto, materiais, medidas, cores, etc."
                />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Validade (dias)
                    </label>
                    <input
                      type="number"
                      value={validityDays}
                      onChange={(e) => setValidityDays(parseInt(e.target.value) || 30)}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Desconto
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={discount.type}
                        onChange={(e) => setDiscount({ ...discount, type: e.target.value })}
                        disabled={!isEditing}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                      >
                        <option value="NONE">Sem desconto</option>
                        <option value="PERCENT">Percentual (%)</option>
                        <option value="FIXED">Valor fixo (R$)</option>
                      </select>
                      {discount.type !== 'NONE' && (
                        <input
                          type="number"
                          value={discount.value}
                          onChange={(e) => setDiscount({ ...discount, value: parseFloat(e.target.value) || 0 })}
                          disabled={!isEditing}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                        />
                      )}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-gray-900">Dados da Empresa</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nome da Empresa
                    </label>
                    <input
                      type="text"
                      value={companyInfo.name}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, name: e.target.value })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                      placeholder="SISTEMA SANTIAGO"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      value={companyInfo.email}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, email: e.target.value })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                      placeholder="moveis@santiago.com.br"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Telefone
                    </label>
                    <input
                      type="text"
                      value={companyInfo.phone}
                      onChange={(e) => setCompanyInfo({ ...companyInfo, phone: e.target.value })}
                      disabled={!isEditing}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-50"
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'items' && (
            <div className="space-y-4">
              {isEditing && (
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="font-medium text-gray-900 mb-3">Adicionar Item</h4>
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-4">
                      <input
                        type="text"
                        value={newItem.name}
                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="Nome do item"
                      />
                    </div>
                    <div className="col-span-3">
                      <input
                        type="text"
                        value={newItem.description}
                        onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="Descrição (opcional)"
                      />
                    </div>
                    <div className="col-span-1">
                      <input
                        type="number"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 1 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="Qtd"
                      />
                    </div>
                    <div className="col-span-2">
                      <select
                        value={newItem.unit}
                        onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        {UNITS.map(u => (
                          <option key={u} value={u}>{u}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <input
                        type="number"
                        value={newItem.unitPrice}
                        onChange={(e) => setNewItem({ ...newItem, unitPrice: parseFloat(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="Valor unitário"
                      />
                    </div>
                    <div className="col-span-12">
                      <button
                        onClick={addItem}
                        className="mt-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Adicionar Item
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Descrição</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qtd</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Un</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Valor Unit</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      {isEditing && <th className="px-4 py-3"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {items.length === 0 ? (
                      <tr>
                        <td colSpan={isEditing ? 7 : 6} className="px-4 py-8 text-center text-gray-500">
                          Nenhum item adicionado
                        </td>
                      </tr>
                    ) : (
                      items.map((item, index) => (
                        <tr key={item.id || index} className={index % 2 === 0 ? '' : 'bg-gray-50'}>
                          <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900">{item.name}</div>
                            {item.description && (
                              <div className="text-xs text-gray-500">{item.description}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{item.quantity}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{item.unit}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            {manualBudgetService.formatCurrency(item.unitPrice)}
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                            {manualBudgetService.formatCurrency(item.quantity * item.unitPrice)}
                          </td>
                          {isEditing && (
                            <td className="px-4 py-3">
                              <button
                                onClick={() => removeItem(index)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan={5} className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                        SUBTOTAL:
                      </td>
                      <td className="px-4 py-3 text-sm font-bold text-gray-900 text-right">
                        {manualBudgetService.formatCurrency(subtotal)}
                      </td>
                      {isEditing && <td></td>}
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'payment' && (
            <div className="space-y-4">
              <div className="bg-emerald-50 p-4 rounded-lg border-2 border-emerald-200">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-emerald-800">
                    Valor Total do Orçamento:
                  </span>
                  <span className="text-2xl font-bold text-emerald-600">
                    {manualBudgetService.formatCurrency(total)}
                  </span>
                </div>
              </div>

              {isEditing && (
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Adicionar Forma de Pagamento</h4>
                  <div className="grid grid-cols-12 gap-3">
                    <div className="col-span-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Tipo
                      </label>
                      <select
                        value={newCondition.type}
                        onChange={(e) => setNewCondition({ ...newCondition, type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                      >
                        <option value="VISTA">À Vista</option>
                        <option value="PARCELADO">Parcelado</option>
                        <option value="ENTRADA_PARCELADO">Entrada + Parcelado</option>
                      </select>
                    </div>
                    <div className="col-span-3">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Nome (opcional)
                      </label>
                      <input
                        type="text"
                        value={newCondition.name}
                        onChange={(e) => setNewCondition({ ...newCondition, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="Ex: PIX desconto"
                      />
                    </div>
                    {newCondition.type !== 'VISTA' && (
                      <>
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Parc.
                          </label>
                          <input
                            type="number"
                            value={newCondition.installments}
                            onChange={(e) => setNewCondition({ ...newCondition, installments: parseInt(e.target.value) || 1 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Juros (% a.m.)
                          </label>
                          <input
                            type="number"
                            value={newCondition.interestRate}
                            onChange={(e) => setNewCondition({ ...newCondition, interestRate: parseFloat(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                          />
                        </div>
                        {newCondition.type === 'ENTRADA_PARCELADO' && (
                          <div className="col-span-2">
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                              Entrada (R$)
                            </label>
                            <input
                              type="number"
                              value={newCondition.downPaymentValue}
                              onChange={(e) => setNewCondition({ ...newCondition, downPaymentValue: parseFloat(e.target.value) || 0 })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        )}
                      </>
                    )}
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Descrição
                      </label>
                      <input
                        type="text"
                        value={newCondition.description}
                        onChange={(e) => setNewCondition({ ...newCondition, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                        placeholder="Ex: 5% desconto"
                      />
                    </div>
                    <div className="col-span-12">
                      <button
                        onClick={addPaymentCondition}
                        className="mt-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Adicionar
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {paymentConditions.length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
                    Nenhuma forma de pagamento adicionada
                  </div>
                ) : (
                  paymentConditions.map((condition, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
                    >
                      <div className="flex items-center">
                        <button
                          onClick={() => setExpandedConditions({
                            ...expandedConditions,
                            [index]: !expandedConditions[index]
                          })}
                          className="flex-1 px-4 py-3 flex items-center justify-between hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-3">
                            {isEditing && (
                              <div className="flex flex-col gap-1">
                                <button
                                  onClick={(e) => { e.stopPropagation(); movePaymentCondition(index, 'up'); }}
                                  disabled={index === 0}
                                  className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                >
                                  <ChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); movePaymentCondition(index, 'down'); }}
                                  disabled={index === paymentConditions.length - 1}
                                  className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                                >
                                  <ChevronDown className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                            <div className={`w-3 h-3 rounded-full ${getConditionColor(condition.type)}`} />
                            <span className="font-medium text-gray-900">
                              {getConditionName(condition)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-lg font-bold text-gray-900">
                              {manualBudgetService.formatCurrency(condition.installmentAmount)}
                              {condition.type !== 'VISTA' && (
                                <span className="text-sm font-normal text-gray-500">/parcela</span>
                              )}
                            </span>
                            {expandedConditions[index] ? (
                              <ChevronUp className="w-5 h-5 text-gray-400" />
                            ) : (
                              <ChevronDown className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                        </button>
                        {isEditing && (
                          <button
                            onClick={() => removePaymentCondition(index)}
                            className="px-3 py-3 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>

                      {expandedConditions[index] && (
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                          {isEditing ? (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Tipo
                                </label>
                                <select
                                  value={condition.type}
                                  onChange={(e) => updateConditionField(index, 'type', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                >
                                  <option value="VISTA">À Vista</option>
                                  <option value="PARCELADO">Parcelado</option>
                                  <option value="ENTRADA_PARCELADO">Entrada + Parcelado</option>
                                </select>
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Nome
                                </label>
                                <input
                                  type="text"
                                  value={condition.name || ''}
                                  onChange={(e) => updateConditionField(index, 'name', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                  placeholder="Nome personalizado"
                                />
                              </div>
                              {condition.type !== 'VISTA' && (
                                <>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Parcelas
                                    </label>
                                    <input
                                      type="number"
                                      value={condition.installments}
                                      onChange={(e) => updateConditionField(index, 'installments', parseInt(e.target.value) || 1)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">
                                      Juros (% a.m.)
                                    </label>
                                    <input
                                      type="number"
                                      step="0.1"
                                      value={condition.interestRate || 0}
                                      onChange={(e) => updateConditionField(index, 'interestRate', parseFloat(e.target.value) || 0)}
                                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                    />
                                  </div>
                                  {condition.type === 'ENTRADA_PARCELADO' && (
                                    <div>
                                      <label className="block text-xs font-medium text-gray-700 mb-1">
                                        Entrada (R$)
                                      </label>
                                      <input
                                        type="number"
                                        value={condition.downPaymentValue || 0}
                                        onChange={(e) => updateConditionField(index, 'downPaymentValue', parseFloat(e.target.value) || 0)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                      />
                                    </div>
                                  )}
                                </>
                              )}
                              <div className={condition.type !== 'VISTA' ? '' : 'col-span-2'}>
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Descrição
                                </label>
                                <input
                                  type="text"
                                  value={condition.description || ''}
                                  onChange={(e) => updateConditionField(index, 'description', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                                  placeholder="Ex: 5% desconto"
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">Tipo:</span>
                                <span className="ml-2 text-gray-900">{condition.type}</span>
                              </div>
                              {condition.type !== 'VISTA' && (
                                <>
                                  <div>
                                    <span className="text-gray-500">Parcelas:</span>
                                    <span className="ml-2 text-gray-900">{condition.installments}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Juros:</span>
                                    <span className="ml-2 text-gray-900">{condition.interestRate}%</span>
                                  </div>
                                </>
                              )}
                              {condition.description && (
                                <div className="col-span-2">
                                  <span className="text-gray-500">Descrição:</span>
                                  <span className="ml-2 text-gray-900">{condition.description}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {activeTab === 'terms' && (
            <div className="space-y-4">
              <div className="bg-emerald-50 p-4 rounded-lg">
                <h4 className="font-medium text-emerald-800 mb-2">Termos e Condições Padrão</h4>
                <p className="text-sm text-emerald-600">
                  Estes termos serão adicionados ao orçamento automaticamente.
                </p>
              </div>

              <div className="space-y-2">
                {terms.map((term, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-gray-200">
                    <span className="text-gray-400">•</span>
                    <span className="flex-1 text-gray-900">{term}</span>
                    {isEditing && (
                      <button
                        onClick={() => setTerms(terms.filter((_, i) => i !== index))}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {isEditing && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    id="new-term"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    placeholder="Novo termo..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        setTerms([...terms, e.target.value.trim()]);
                        e.target.value = '';
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      const input = document.getElementById('new-term');
                      if (input.value.trim()) {
                        setTerms([...terms, input.value.trim()]);
                        input.value = '';
                      }
                    }}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                  >
                    Adicionar
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ManualBudgetCreator;